import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BlockListOrderOnboardingComponent } from 'components/sale/order-onboarding/blocklist-order-onboarding.component';
import { BlockListQualificationCallComponent } from 'components/sale/unassigned-qualification-calls/blocklist-qualification-call.component';
import { DraggableDirective } from 'directives/draggable.directive';
import { BlockListQuestionnaireComponent } from './blocklist-questionnaire.component';
import { BlockListUserComponent } from './blocklist-user.component';

@Component({
	selector: 'blocklist',
	templateUrl: './blocklist.component.html',
	styleUrls: ['./blocklist.component.scss'],
	imports: [DraggableDirective, NgClass, BlockListUserComponent, BlockListQualificationCallComponent, BlockListQuestionnaireComponent, BlockListOrderOnboardingComponent]
})
export class BlockListComponent {

	@ViewChild('ghostBlock') ghostBlock!: ElementRef<HTMLInputElement>;

	@Input() items: any[] = [];
	@Input() blockListType = '';
	@Input() dragEnabled = false;
	@Input() dropEnabled = false;
	@Input() dragging = false;

	@Output() onDrop = new EventEmitter<BlockDragDropEventResponse>();
	@Output() onDrag = new EventEmitter<boolean>();
	@Output() onSelect = new EventEmitter<string>();

	private currentDropTarget: Element | undefined;
	public selectedItemIdentifier = '';

	get selectable(): boolean {
		return !this.dragEnabled && !this.dropEnabled;
	}

	@Input()
	set selectedItem(item: any) {
		if (item === undefined) return;
		let id = this.getItemIdentifierFromItem(item);
		this.selectItem(id, false);
	}
	get selectedItem() {
		return this.items.find(item => this.getItemIdentifierFromItem(item) === this.selectedItemIdentifier);
	}

	selectItem(itemIdentifier: string, emit: boolean = true) {
		this.selectedItemIdentifier = itemIdentifier;
		if (emit)
			this.onSelect.emit(itemIdentifier);
	}

	dragBlock(event: DragEvent) {
		this.ghostBlock.nativeElement.style.display = 'block';
		this.ghostBlock.nativeElement.style.top = (event.clientY - this.ghostBlock.nativeElement.offsetHeight + 10) + 'px';
		this.ghostBlock.nativeElement.style.left = (event.clientX - this.ghostBlock.nativeElement.offsetWidth / 2) + 'px';
	}

	dropBlock(event: DragEvent) {
		this.dragging = false;
		this.onDrag.emit(this.dragging);
		this.ghostBlock.nativeElement.style.display = 'none';
		if (this.currentDropTarget) this.currentDropTarget.classList.remove('droppable');
		if (this.dropEnabled) {
			if (event.dataTransfer && this.currentDropTarget) {
				let response = new BlockDragDropEventResponse();
				response.dragId = event.dataTransfer.getData('dragId');
				response.dropId = this.getItemIdentifierFromItemFromElement(this.currentDropTarget);
				this.onDrop.emit(response);
			}
		}
		this.currentDropTarget = undefined;
	}

	onDragOver(event: any) {
		event.stopPropagation();
		event.preventDefault();
		if (this.dropEnabled) {
			this.currentDropTarget = event.path.find((element: any) => {
				if (element.className && element.className.indexOf('block-item') !== -1) return element;
				return undefined;
			});
			if (this.currentDropTarget) this.currentDropTarget.classList.add('droppable');
		}
	}

	onDragOut(event: any) {
		event.stopPropagation();
		event.preventDefault();
		if (this.currentDropTarget) this.currentDropTarget.classList.remove('droppable');
	}

	onDragStart(event: any) {
		if (this.dragEnabled) {
			this.dragging = true;
			this.onDrag.emit(this.dragging);
			if (event.dataTransfer) {
				event.dataTransfer.setDragImage(document.createElement('span'), 0, 0);
				event.dataTransfer.setData('dragId', this.getItemIdentifierFromItemFromElement(this.getTargetFromEvent(event)));
			}
		} else {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	@Input()
	getItemIdentifierFromItem(item: any): string {
		switch (this.blockListType) {
			case 'user':
				return item.userId;
			case 'qualificationCall':
				return item.taskActionId;
			case 'questionnaire':
				return item.id;
			case 'orderOnboarding':
				return item.taskflowId;
		}
		return '';
	}

	getItemIdentifierFromItemFromElement(element: Element): string {
		return element.hasAttribute('itemIdentifier') ? element.getAttribute('itemIdentifier')! : '';
	}

	getTargetFromEvent(event: Event): Element {
		return (event.target || event.srcElement || event.currentTarget) as Element;
	}
}

export class BlockDragDropEventResponse {
	dragId?: string;
	dropId?: string;
}